import { FarmnetModuleService } from './farmnet-module.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ModuleConfig } from '../models/module-config.interface';

declare const window: any;

@Injectable({
    providedIn: 'root'
})
export class FarmnetModuleLoadingService {

    constructor(private farmnetModuleService: FarmnetModuleService, private router: Router) {}

    loadModuleFactory(module: ModuleConfig): () => Promise<any> {
        return () => {
            return this.loadModule(module)
                .catch((rejection) => {
                    console.warn('error while loading Farmnet module. Trying again with updated moduleInfo', rejection);
                    return this.farmnetModuleService.loadAndStoreModuleInfo().toPromise()
                        .then(() => {
                            const updatedModule = this.farmnetModuleService.findNgxModule(module.name);

                            if(updatedModule) {
                                return this.loadModule(updatedModule);
                            } else {
                                throw `module not found in updated moduleInfo anymore ${module.name}`;
                            }
                        });
                })
                .then((loadedModules) => {
                    console.log('loaded', loadedModules);
                    return loadedModules[0];
                });
        };
    }

    private loadModule(module: ModuleConfig): Promise<any> {
        const jsFiles = module.files.filter((path: string) => path.endsWith('.js'));
        const cssFiles = module.files.filter((path: string) => path.endsWith('.css'));

        this.removeOldCss(module.name);

        const promises = cssFiles.map((cssFile: string) => {
            return this.loadCss(cssFile, module.name);
        });

        if (module.type === 'ngx-angular') {
            //meant for namedspaced angular modules
            promises.unshift(this.loadNamedJsModule(jsFiles, module));
        } else {
            promises.unshift(this.loadUnNamedJsModule(jsFiles, module));
        }

        return Promise.all(promises)
    }

    private loadUnNamedJsModule(jsFiles, module: ModuleConfig) {
        return new Promise((resolve,reject) => {
            window.requirejs(jsFiles, resolve, (error) => {
                this.handleRequireError(error);
                reject(error);
            });
        }).then((requireJsModule) => {
            return this.resolveModule(requireJsModule, module);
        });
    }

    private loadNamedJsModule(jsFiles, module: ModuleConfig) {
        return new Promise((resolve, reject) => {
            window.requirejs(jsFiles,
                () => {
                    window.requirejs([module.library], resolve, reject);
                },
                (error) => {
                    this.handleRequireError(error);
                    reject(error);
                });
        }).then((requireJsModule) => {
            return this.resolveModule(requireJsModule, module);
        });
    }

    private handleRequireError(error) {
        if(error.requireModules) {
            error.requireModules.forEach(errorModule => {
                // remove errored module or requirejs will not try to reload it
                window.requirejs.undef(errorModule);
            })
        }
    }

    private resolveModule(requireJsModule, module: ModuleConfig) {
        const type = requireJsModule[module.library];
        if (type) {
            return type;
        } else {
            throw `trying to load module [${module.name}] but it does not contain [${module.library}]`;
        }
    }

    private loadCss(url: string, moduleName: string): Promise<string> {
        return new Promise((resolve, reject) => {
            const link: HTMLLinkElement = document.createElement('link');
            link.type = 'text/css';
            link.rel = 'stylesheet';
            link.href = url;
            link.onerror = (event: Event | string, source?: string, fileno?: number, columnNumber?: number, error?: Error) => {
                reject(event);
            };
            link.onload = () => {
                resolve(url);
            };
            link.setAttribute('data-module-loading-css', moduleName);
            document.getElementsByTagName('head')[0].appendChild(link);
        });
    }

    private removeOldCss(moduleName: string) {
        const elements = document.querySelectorAll(`link[data-module-loading-css="${moduleName}"]`);

        Array.from(elements).forEach(element => {
            if(element.parentNode) {
                element.parentNode.removeChild(element);
            }
        });
    }
}
